import i18n from 'i18next'

/**
 * Pure schema-title localization helpers, copied from @/utils/schemaLocalization so
 * the AiChatBot tools tree has no @/utils app-internal import. Behaviour is
 * byte-identical — depends only on i18next (an npm lib, no host coupling).
 *
 * Only the subset the tools need (getLocalizedTitle for filter-normalizer,
 * getLocalizedSchemaEnumValues for field-mapper) is copied here.
 */
type LocalizedTitles = Record<string, string | undefined>

type LocalizableTitle = {
  title?: string
  localizedTitles?: LocalizedTitles
}

type LocalizableEnumValue = LocalizableTitle & {
  value?: string
}

const getLanguageCandidates = () => {
  const language = (i18n.resolvedLanguage || i18n.language || 'en')
    .trim()
    .toLowerCase()

  return language === 'en' ? ['en'] : [language, 'en']
}

export const getLocalizedTitle = <T extends LocalizableTitle>(
  item?: T,
  fallback = '',
) => {
  if (!item) return fallback

  const localizedTitles = item.localizedTitles || {}

  for (const language of getLanguageCandidates()) {
    if (!language) continue

    const localizedTitle = localizedTitles[language]
    if (localizedTitle) return localizedTitle
  }

  return fallback
}

export const getLocalizedSchemaEnumValues = <T extends LocalizableEnumValue>(
  enumValues?: T[],
): T[] | undefined => {
  if (!enumValues) return undefined

  return enumValues.map((enumValue) => ({
    ...enumValue,
    title: getLocalizedTitle(enumValue, enumValue.value || ''),
  })) as T[]
}
